// Stades de Prochaska et DiClemente (Transthéorétique)
// 1. Précontemplation - Ne pense pas à arrêter
// 2. Contemplation - Pense à arrêter mais pas prêt
// 3. Préparation - Se prépare à arrêter
// 4. Action - En train d'arrêter
// 5. Maintien - A arrêté et maintient

export const prochaskaQuestions = [
  {
    id: 1,
    text_fr: "Avez-vous l'intention d'arrêter de fumer dans les 6 prochains mois ?",
    text_en: "Do you intend to quit smoking in the next 6 months?",
    options: [
      { label_fr: "Non, je n'y pense pas", label_en: "No, I don't think about it", stage: "precontemplation", points: 1 },
      { label_fr: "Oui, j'y pense", label_en: "Yes, I think about it", stage: "contemplation", points: 2 },
      { label_fr: "Oui, dans le mois qui vient", label_en: "Yes, in the next month", stage: "preparation", points: 3 }
    ]
  },
  {
    id: 2,
    text_fr: "Avez-vous déjà fait une tentative sérieuse d'arrêt (au moins 24h sans fumer) ?",
    text_en: "Have you already made a serious quit attempt (at least 24h without smoking)?",
    options: [
      { label_fr: "Non, jamais", label_en: "No, never", stage: "precontemplation", points: 1 },
      { label_fr: "Oui, mais j'ai repris", label_en: "Yes, but I started again", stage: "contemplation", points: 2 },
      { label_fr: "Oui, récemment", label_en: "Yes, recently", stage: "preparation", points: 3 }
    ]
  },
  {
    id: 3,
    text_fr: "Actuellement, fumez-vous ?",
    text_en: "Currently, do you smoke?",
    options: [
      { label_fr: "Oui, régulièrement", label_en: "Yes, regularly", stage: "precontemplation", points: 1 },
      { label_fr: "Oui, mais je réduis", label_en: "Yes, but I'm reducing", stage: "preparation", points: 2 },
      { label_fr: "Non, j'ai arrêté depuis moins de 6 mois", label_en: "No, I quit less than 6 months ago", stage: "action", points: 4 },
      { label_fr: "Non, j'ai arrêté depuis plus de 6 mois", label_en: "No, I quit more than 6 months ago", stage: "maintenance", points: 5 }
    ]
  },
  {
    id: 4,
    text_fr: "Avez-vous fixé une date pour arrêter de fumer ?",
    text_en: "Have you set a quit date?",
    options: [
      { label_fr: "Non, pas du tout", label_en: "No, not at all", stage: "precontemplation", points: 1 },
      { label_fr: "J'y réfléchis", label_en: "I'm thinking about it", stage: "contemplation", points: 2 },
      { label_fr: "Oui, dans les 30 prochains jours", label_en: "Yes, in the next 30 days", stage: "preparation", points: 3 },
      { label_fr: "J'ai déjà arrêté", label_en: "I already quit", stage: "action", points: 4 }
    ]
  },
  {
    id: 5,
    text_fr: "Comment décririez-vous votre attitude face à l'arrêt du tabac ?",
    text_en: "How would you describe your attitude towards quitting?",
    options: [
      { label_fr: "Je ne veux pas arrêter", label_en: "I don't want to quit", stage: "precontemplation", points: 1 },
      { label_fr: "Je suis ambivalent(e)", label_en: "I'm ambivalent", stage: "contemplation", points: 2 },
      { label_fr: "Je suis déterminé(e) à arrêter", label_en: "I'm determined to quit", stage: "preparation", points: 3 },
      { label_fr: "Je suis en cours d'arrêt", label_en: "I'm in the process of quitting", stage: "action", points: 4 },
      { label_fr: "J'ai réussi à arrêter", label_en: "I succeeded in quitting", stage: "maintenance", points: 5 }
    ]
  }
];

export const getStageFromScore = (totalPoints, language) => {
  const avgScore = totalPoints / 5;
  
  if (avgScore <= 1.4) {
    return {
      stage: "precontemplation",
      name_fr: "Précontemplation",
      name_en: "Precontemplation",
      description_fr: "Vous n'envisagez pas d'arrêter de fumer pour le moment. C'est une étape normale du processus. Prenez le temps de réfléchir aux avantages de l'arrêt.",
      description_en: "You are not considering quitting smoking at this time. This is a normal stage of the process. Take time to think about the benefits of quitting.",
      color: "bg-red-100",
      textColor: "text-red-800",
      advice_fr: [
        "Informez-vous sur les risques du tabac",
        "Listez les raisons qui pourraient vous motiver à arrêter",
        "Parlez avec des personnes qui ont arrêté"
      ],
      advice_en: [
        "Learn about the risks of tobacco",
        "List reasons that might motivate you to quit",
        "Talk to people who have quit"
      ]
    };
  } else if (avgScore <= 2.4) {
    return {
      stage: "contemplation",
      name_fr: "Contemplation",
      name_en: "Contemplation",
      description_fr: "Vous pensez à arrêter de fumer mais vous n'êtes pas encore prêt(e). Vous pesez le pour et le contre. C'est une étape importante de réflexion.",
      description_en: "You are thinking about quitting but you're not ready yet. You're weighing the pros and cons. This is an important stage of reflection.",
      color: "bg-orange-100",
      textColor: "text-orange-800",
      advice_fr: [
        "Identifiez vos freins et vos motivations",
        "Calculez le coût de votre consommation",
        "Imaginez votre vie sans tabac"
      ],
      advice_en: [
        "Identify your barriers and motivations",
        "Calculate the cost of your consumption",
        "Imagine your life without tobacco"
      ]
    };
  } else if (avgScore <= 3.4) {
    return {
      stage: "preparation",
      name_fr: "Préparation",
      name_en: "Preparation",
      description_fr: "Vous êtes prêt(e) à passer à l'action ! Vous avez décidé d'arrêter et vous vous préparez. C'est le moment de planifier votre arrêt.",
      description_en: "You're ready to take action! You've decided to quit and you're preparing. It's time to plan your quit.",
      color: "bg-yellow-100",
      textColor: "text-yellow-800",
      advice_fr: [
        "Fixez une date d'arrêt dans les 30 jours",
        "Préparez votre environnement (jetez briquets, cendriers)",
        "Informez votre entourage de votre décision",
        "Consultez un professionnel de santé"
      ],
      advice_en: [
        "Set a quit date within 30 days",
        "Prepare your environment (throw away lighters, ashtrays)",
        "Tell your family and friends about your decision",
        "Consult a healthcare professional"
      ]
    };
  } else if (avgScore <= 4.4) {
    return {
      stage: "action",
      name_fr: "Action",
      name_en: "Action",
      description_fr: "Bravo ! Vous êtes en train d'arrêter de fumer. C'est la phase la plus difficile mais aussi la plus gratifiante. Tenez bon !",
      description_en: "Congratulations! You are quitting smoking. This is the most difficult but also the most rewarding phase. Stay strong!",
      color: "bg-green-100",
      textColor: "text-green-800",
      advice_fr: [
        "Utilisez les exercices de respiration pour gérer les envies",
        "Évitez les situations à risque",
        "Récompensez-vous pour chaque jour sans tabac",
        "N'hésitez pas à demander de l'aide"
      ],
      advice_en: [
        "Use breathing exercises to manage cravings",
        "Avoid risky situations",
        "Reward yourself for each smoke-free day",
        "Don't hesitate to ask for help"
      ]
    };
  } else {
    return {
      stage: "maintenance",
      name_fr: "Maintien",
      name_en: "Maintenance",
      description_fr: "Félicitations ! Vous avez arrêté de fumer depuis plus de 6 mois. Continuez à être vigilant(e) et à maintenir vos bonnes habitudes.",
      description_en: "Congratulations! You have quit smoking for more than 6 months. Continue to be vigilant and maintain your good habits.",
      color: "bg-emerald-100",
      textColor: "text-emerald-800",
      advice_fr: [
        "Restez vigilant face aux situations à risque",
        "Continuez vos nouvelles habitudes saines",
        "Aidez d'autres fumeurs qui veulent arrêter",
        "Célébrez vos réussites !"
      ],
      advice_en: [
        "Stay vigilant in risky situations",
        "Continue your new healthy habits",
        "Help other smokers who want to quit",
        "Celebrate your successes!"
      ]
    };
  }
};
